import React, {useState, useEffect} from 'react';
import http from '../../libs/http/http';
import MyModal from '../../libs/MyModal/MyModal';
import Button from '../../libs/Button/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
} from 'chart.js';
import {Bar} from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// Not real security (backend still requires admin_auth on every call here) -
// just keeps this page from looking like a plain, memorable, guessable url.
// Someone glancing at a shared screen sees a 60-char query param, not
// something they could retype from memory or find by trying /api-clients.
const PAGE_KEY = 'hSjItp9HgN3hch0G9TO2wjFE6rU8MlzG3h18iPMyZFbhWilpFOaN1X4Kf3';

// One entry per scopable entity type - drives the picker in the create form,
// the picker+save in the details modal, and the count columns in the table.
const ENTITY_KINDS = [
    {field: 'allowedModules', label: 'Модули', apiListPath: '/course-module', saveSuffix: 'modules'},
    {field: 'allowedQuizzes', label: 'Квизы', apiListPath: '/quiz', saveSuffix: 'quizzes'},
    {field: 'allowedCourses', label: 'Курсы', apiListPath: '/course', saveSuffix: 'courses'},
];

function ApiClients() {
    let hasKey = new URLSearchParams(window.location.search).get('t') === PAGE_KEY;
    return hasKey ? <ApiClientsPage/> : null;
}

function ApiClientsPage() {
    let [items, setItems] = useState([]);
    let [catalogs, setCatalogs] = useState({}); // field -> items[]
    let [newName, setNewName] = useState('');
    let [newIds, setNewIds] = useState({}); // field -> selected ids[]
    let [revealed, setRevealed] = useState(null);
    let [statsFor, setStatsFor] = useState(null);
    let [statsRows, setStatsRows] = useState([]);
    let [detailsFor, setDetailsFor] = useState(null);
    let [detailsIds, setDetailsIds] = useState({}); // field -> selected ids[]

    function load() {
        http.get('/admin/api-clients')
            .then(r => setItems(r.items || []))
    }

    useEffect(load, []);
    useEffect(() => {
        ENTITY_KINDS.forEach(kind => {
            http.get(kind.apiListPath, {per_page: 500})
                .then(r => setCatalogs(c => ({...c, [kind.field]: r.items || []})))
        })
    }, []);

    function onCreate(e) {
        e.preventDefault();
        if (!newName.trim()) {
            return;
        }
        http.post('/admin/api-clients', {name: newName, ...newIds})
            .then(r => {
                setNewName('');
                setNewIds({});
                setRevealed({client_id: r.client_id, client_secret: r.client_secret});
                load();
            })
    }

    function onToggleActive(item) {
        http.post('/admin/api-clients/' + item._id + '/toggle-active', {})
            .then(load)
    }

    function onShowStats(item) {
        setStatsFor(item);
        http.get('/admin/api-clients/' + item._id + '/stats')
            .then(r => setStatsRows(r.items || []))
    }

    function onShowDetails(item) {
        setDetailsFor(item);
        let ids = {};
        ENTITY_KINDS.forEach(kind => ids[kind.field] = item[kind.field] || []);
        setDetailsIds(ids);
    }

    function onSaveScope(kind) {
        http.post('/admin/api-clients/' + detailsFor._id + '/' + kind.saveSuffix, {[kind.field]: detailsIds[kind.field]})
            .then(() => load())
    }

    return (<div>
        <h3>API клиенты</h3>

        <form className="mb-20" onSubmit={onCreate}>
            <input
                type="text"
                placeholder="Название клиента"
                value={newName}
                onChange={e => setNewName(e.target.value)}
            />
            {' '}
            <Button type="submit">Создать</Button>

            {ENTITY_KINDS.map(kind => (
                <EntityPicker
                    key={kind.field}
                    label={`${kind.label}, доступные этому клиенту`}
                    placeholder={`Ничего не выбрано — доступа не будет`}
                    options={catalogs[kind.field] || []}
                    selectedIds={newIds[kind.field] || []}
                    onChange={ids => setNewIds(v => ({...v, [kind.field]: ids}))}
                />
            ))}
        </form>

        <table className="table table-bordered table-striped">
            <thead>
            <tr>
                <th>Название</th>
                <th>Client ID</th>
                <th>Активен</th>
                {ENTITY_KINDS.map(kind => <th key={kind.field}>{kind.label}</th>)}
                <th>Запросов за 30 дней</th>
                <th>Последнее использование</th>
                <th></th>
            </tr>
            </thead>
            <tbody>
            {items.map(item => {
                let usage = item.usage30d || {};
                return (<tr key={item._id}>
                    <td>{item.name}</td>
                    <td><code>{item.clientId}</code></td>
                    <td>{item.active ? 'да' : 'нет'}</td>
                    {ENTITY_KINDS.map(kind => <td key={kind.field}>{(item[kind.field] || []).length}</td>)}
                    <td>{usage.requestCount || 0}</td>
                    <td>{usage.lastUsed || '—'}</td>
                    <td>
                        <Button onClick={cb => {
                            onShowDetails(item);
                            cb()
                        }}>Детали</Button>
                        {' '}
                        <Button onClick={cb => {
                            onShowStats(item);
                            cb()
                        }}>График</Button>
                        {' '}
                        <Button color={item.active ? 2 : 3} onClick={cb => {
                            onToggleActive(item);
                            cb()
                        }}>{item.active ? 'Деактивировать' : 'Активировать'}</Button>
                    </td>
                </tr>)
            })}
            </tbody>
        </table>

        <MyModal isOpen={!!revealed} title="Новый клиент создан" onClose={() => setRevealed(null)}>
            {revealed && <div>
                <p>Секрет показывается один раз, сохраните его сейчас — повторно его получить будет нельзя.</p>
                <p>client_id: <code>{revealed.client_id}</code></p>
                <p>client_secret: <code>{revealed.client_secret}</code></p>
            </div>}
        </MyModal>

        <MyModal isOpen={!!statsFor} title={statsFor ? 'Статистика: ' + statsFor.name : ''} onClose={() => setStatsFor(null)}>
            {statsFor && <UsageChart rows={statsRows}/>}
        </MyModal>

        <MyModal isOpen={!!detailsFor} title={detailsFor ? 'Клиент: ' + detailsFor.name : ''} onClose={() => setDetailsFor(null)}>
            {detailsFor && <div>
                <p>client_id: <code>{detailsFor.clientId}</code></p>
                <p>Активен: {detailsFor.active ? 'да' : 'нет'}</p>
                <p>Создан: {new Date(detailsFor.cd).toLocaleString()}</p>

                {ENTITY_KINDS.map(kind => (<div key={kind.field}>
                    <EntityPicker
                        label={`Доступные: ${kind.label}`}
                        placeholder="Добавить"
                        options={catalogs[kind.field] || []}
                        selectedIds={detailsIds[kind.field] || []}
                        onChange={ids => setDetailsIds(v => ({...v, [kind.field]: ids}))}
                    />
                    <Button className="mt-10" onClick={cb => {
                        onSaveScope(kind);
                        cb()
                    }}>Сохранить: {kind.label}</Button>
                </div>))}
            </div>}
        </MyModal>
    </div>)
}

function EntityPicker(props) {
    let {label, placeholder, options, selectedIds, onChange} = props;
    return (<div className="mt-10" style={{maxWidth: 500}}>
        <Autocomplete
            multiple
            options={options}
            getOptionLabel={option => `${option.name} (#${option._id})`}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={options.filter(option => selectedIds.includes(option._id))}
            filterSelectedOptions
            onChange={(e, values) => onChange(values.map(v => v._id))}
            renderInput={params => <TextField {...params} label={label} placeholder={placeholder}/>}
        />
    </div>)
}

function UsageChart(props) {
    let {rows} = props;
    let data = {
        labels: rows.map(r => r.odb),
        datasets: [{
            // Same metric as the "Запросов за 30 дней" column (requestCount only -
            // actual calls to /api/public/course-modules) - deliberately excludes
            // issueCount/refreshCount (token issuance/refresh) so the two numbers
            // on this page always agree.
            label: 'Запросы к API за день',
            data: rows.map(r => r.requestCount || 0),
            backgroundColor: 'rgba(53, 162, 235, 0.5)',
        }]
    };

    return <Bar data={data} options={{plugins: {legend: {display: false}}}}/>
}

export default ApiClients
